import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import RaycasterClass from '@modules/three/common/raycast.ts';
import { threeStore } from '@modules/three/store/threeStore.ts';
import { loadThreeModel } from '@modules/three/utils/loadThreeModel';
import HalfEdgeMesh from '@/modules/three/common/halfEdgeMesh/HalfEdgeMesh.js';
import { BufferGeometry } from 'three';
import Gizmo from '@modules/three/common/transformMesh/Gizmo';
import { SculptingTool } from '../tools/SculptingTool';
import { toolManager } from '../tools/ToolManager';

export interface ThreeRootState {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  raycaster: RaycasterClass;
  container: HTMLElement;
  gizmo: Gizmo;
}

export class BaseThreeScene {
  public readonly root: ThreeRootState;
  private _animationFrameId: number | null = null;
  private readonly _onPointerEvent: (event: PointerEvent) => void;
  private _isDisposed = false;
  private readonly clock = new THREE.Clock();

  constructor(container: HTMLElement) {
    threeStore.setState({ isLoading: true });
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    const controls = new OrbitControls(camera, renderer.domElement);
    const raycaster = new RaycasterClass();
    const gizmo = new Gizmo(camera, renderer.domElement);
    this.root = {
      scene,
      camera,
      renderer,
      controls,
      raycaster,
      container,
      gizmo,
    };

    this._onPointerEvent = (event) => {
      raycaster.raycast(event, this.root);
    };

    this.initRenderer();
    this.initCamera();
    this.initLights();
    this.initEvents();
    this.initGizmo();
    threeStore.getState().rootState = this.root; //TODO call dispose when this will be used in all components
    toolManager.register(new SculptingTool());
    threeStore.setState({ isLoading: false });
  }

  private initRenderer() {
    const { renderer, container } = this.root;
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);
  }

  private initCamera() {
    const { camera, controls } = this.root;
    camera.position.set(0, 15, 50);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.update();
  }

  private initLights() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(8, 40, 20);

    const key2 = new THREE.DirectionalLight(0xffffff, 1);
    key2.position.set(-20, -10, -10);
    this.root.scene.add(key2);

    key.castShadow = true;
    key.shadow.mapSize.set(512, 512);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 200;
    key.shadow.radius = 2;
    key.shadow.bias = -0.0005;

    this.root.scene.add(ambientLight, key);
  }

  private initEvents() {
    const { renderer } = this.root;
    const dom = renderer.domElement;

    dom.addEventListener('pointerdown', this._onPointerEvent);
    dom.addEventListener('pointermove', this._onPointerEvent);
    dom.addEventListener('pointerup', this._onPointerEvent);
  }

  private initGizmo() {
    const { gizmo } = this.root;
    this.root.scene.add(gizmo.getHelper());
  }

  protected beforeRender(_delta: number): void {
    // subclasses can override to hook into the render loop
  }

  public startRendering() {
    const animate = () => {
      if (this._isDisposed) {
        return;
      }
      this._animationFrameId = requestAnimationFrame(animate);
      const { renderer, scene, camera, controls } = this.root;
      if (!scene || !camera || !renderer) {
        return;
      }
      controls.update();
      const delta = this.clock.getDelta();
      this.beforeRender(delta);
      renderer.render(scene, camera);
    };

    animate();
  }

  public dispose() {
    if (this._isDisposed) {
      return;
    }
    this._isDisposed = true;
    const { renderer, controls, container } = this.root;

    if (this._animationFrameId !== null) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }
    if (renderer.domElement.parentElement === container) {
      container.removeChild(renderer.domElement);
    }

    controls.dispose();
    renderer.dispose();

    this.root.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry.dispose();
        if (object.material instanceof THREE.Material) {
          object.material.dispose();
        } else if (Array.isArray(object.material)) {
          object.material.forEach((mat) => mat.dispose());
        }
      }
    });

    renderer.domElement.removeEventListener('pointerdown', this._onPointerEvent);
    renderer.domElement.removeEventListener('pointermove', this._onPointerEvent);
    renderer.domElement.removeEventListener('pointerup', this._onPointerEvent);
  }

  public static async loadMeshes(files: File[] | string[]): Promise<Array<HalfEdgeMesh>> {
    threeStore.getState().setIsLoading(true);
    const meshes = await Promise.all(files.map((file) => loadThreeModel(file)));
    const result = meshes.map((mesh) => new HalfEdgeMesh(mesh));
    threeStore.getState().setIsLoading(false);
    return result;
  }

  public static async loadGeometries(files: File[] | string[]): Promise<BufferGeometry[]> {
    threeStore.getState().setIsLoading(true);
    const geoms = await Promise.all(files.map((f) => loadThreeModel(f)));
    threeStore.getState().setIsLoading(false);
    return geoms;
  }

  public getRootState(): ThreeRootState {
    return this.root;
  }
}
